import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';

// Admin-only. Categories group quizzes by subject area; granting a user
// 'manager' on a category lets them run every exam inside it without seeing
// anything else in the portal.
const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editModal, setEditModal] = useState(null);      // { id?, name, description }
  const [managerModal, setManagerModal] = useState(null); // { category, managers[], selectedUserId }
  const [deleteModal, setDeleteModal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [cats, usrs] = await Promise.all([api.get('/categories'), api.get('/users')]);
      setCategories(cats.data);
      setUsers(usrs.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const saveCategory = async () => {
    if (!editModal.name?.trim()) { alert('Please enter a category name'); return; }
    setBusy(true);
    try {
      const payload = { name: editModal.name.trim(), description: editModal.description || '' };
      if (editModal.id) await api.put(`/categories/${editModal.id}`, payload);
      else await api.post('/categories', payload);
      setEditModal(null);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save category');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/categories/${deleteModal.id}`);
      setDeleteModal(null);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete category');
    } finally {
      setBusy(false);
    }
  };

  const openManagers = async (category) => {
    try {
      const res = await api.get(`/categories/${category.id}/managers`);
      setManagerModal({ category, managers: res.data, selectedUserId: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load managers');
    }
  };

  const grantAccess = async () => {
    if (!managerModal.selectedUserId) { alert('Select a user first'); return; }
    setBusy(true);
    try {
      await api.post(`/categories/${managerModal.category.id}/managers`, {
        user_id: parseInt(managerModal.selectedUserId, 10)
      });
      await openManagers(managerModal.category);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to grant access');
    } finally {
      setBusy(false);
    }
  };

  const revokeAccess = async (userId) => {
    if (!window.confirm('Remove this person’s access to the category?')) return;
    setBusy(true);
    try {
      await api.delete(`/categories/${managerModal.category.id}/managers/${userId}`);
      await openManagers(managerModal.category);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke access');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="card"><div className="text-lg">Loading categories...</div></div>;

  // Admins already reach everything, so they are not offerable as managers.
  const grantable = users.filter(
    (u) => u.role !== 'admin' && u.is_active &&
           !(managerModal?.managers || []).some((m) => m.user_id === u.id)
  );

  return (
    <div>
      <div className="flex flex-between flex-center" style={{ marginBottom: '24px' }}>
        <div>
          <h2>Exam Categories</h2>
          <p className="text-muted text-sm" style={{ marginTop: '4px' }}>
            Group exams by subject, then give a team member control of just that group.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditModal({ name: '', description: '' })}>
          + New Category
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {categories.length === 0 ? (
          <p className="text-muted">No categories yet. Create one to start grouping exams.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Description</th>
                <th style={{ textAlign: 'center' }}>Exams</th>
                <th style={{ textAlign: 'center' }}>Managers</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td className="text-muted text-sm">{c.description || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{c.quiz_count}</td>
                  <td style={{ textAlign: 'center' }}>{c.manager_count}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => openManagers(c)}>
                      Manage Access
                    </button>{' '}
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => setEditModal({ id: c.id, name: c.name, description: c.description || '' })}
                    >
                      Edit
                    </button>{' '}
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(c)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* create / edit */}
      {editModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setEditModal(null))}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editModal.id ? 'Edit Category' : 'New Category'}</h3>
              <button className="modal-close" onClick={() => setEditModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={editModal.name}
                  onChange={(e) => setEditModal({ ...editModal, name: e.target.value })}
                  placeholder="e.g. Product Training"
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  rows="2"
                  value={editModal.description}
                  onChange={(e) => setEditModal({ ...editModal, description: e.target.value })}
                  placeholder="Who runs these exams, and what they cover"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCategory} disabled={busy}>
                {busy ? 'Saving...' : (editModal.id ? 'Save Changes' : 'Create Category')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* managers */}
      {managerModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setManagerModal(null))}>
          <div className="modal" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Access to &ldquo;{managerModal.category.name}&rdquo;</h3>
              <button className="modal-close" onClick={() => setManagerModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-muted" style={{ marginBottom: '16px' }}>
                These people can create and run every exam in this category, and see its results.
                They cannot see exams in any other category. Administrators always have access.
              </p>

              {managerModal.managers.length === 0 ? (
                <p className="text-muted">Nobody has been given access yet.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr><th>Name</th><th>Email</th><th style={{ textAlign: 'right' }}></th></tr>
                  </thead>
                  <tbody>
                    {managerModal.managers.map((m) => (
                      <tr key={m.user_id}>
                        <td>{m.name}</td>
                        <td className="text-sm text-muted">{m.email}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-sm btn-danger" onClick={() => revokeAccess(m.user_id)} disabled={busy}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>Give access to</label>
                <div className="flex gap-2">
                  <select
                    style={{ flex: 1 }}
                    value={managerModal.selectedUserId}
                    onChange={(e) => setManagerModal({ ...managerModal, selectedUserId: e.target.value })}
                  >
                    <option value="">Select a user...</option>
                    {grantable.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                  <button className="btn btn-primary" onClick={grantAccess} disabled={busy || !managerModal.selectedUserId}>
                    Grant
                  </button>
                </div>
                {grantable.length === 0 && (
                  <p className="text-sm text-muted" style={{ marginTop: '8px' }}>
                    No eligible users. Create an examiner account first under Users.
                  </p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setManagerModal(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* delete */}
      {deleteModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setDeleteModal(null))}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Category</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Delete &ldquo;{deleteModal.name}&rdquo;?</p>
              {deleteModal.quiz_count > 0 && (
                <div className="alert alert-error" style={{ marginTop: '12px' }}>
                  This category still contains {deleteModal.quiz_count} exam(s). Move them to another
                  category first, or they would become visible to administrators only.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={busy || deleteModal.quiz_count > 0}>
                {busy ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
